import React from 'react';
import useStyles from './BlurSkeleton.styles';

type Variant = 'text' | 'rect' | 'circle';

interface IProps {
  variant?: Variant;
  /** Omit to let `className` (or the variant default) control the size. */
  width?: number | string;
  height?: number | string;
  /** Overrides the variant's default corner radius. */
  radius?: number | string;
  /** Staggers the pulse so stacked placeholders do not breathe in unison. */
  delay?: number;
  /** Opacity-only pulse. Use in dense grids where animating blur is costly. */
  subtle?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** A single pulsing, blurred placeholder block. */
const BlurSkeleton: React.FC<IProps> = ({
  variant = 'text',
  width,
  height,
  radius,
  delay = 0,
  subtle = false,
  className,
  style,
}) => {
  const classes = useStyles();
  const circleSize = variant === 'circle' ? (height ?? width) : undefined;

  return (
    <span
      aria-hidden="true"
      className={[classes.skeleton, classes[variant], subtle && classes.subtle, className]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...(width !== undefined ? { width } : null),
        ...(height !== undefined ? { height } : null),
        ...(circleSize !== undefined ? { width: circleSize, height: circleSize } : null),
        ...(radius !== undefined ? { borderRadius: radius } : null),
        ...(delay ? { animationDelay: `${delay}ms` } : null),
        ...style,
      }}
    />
  );
};

export default BlurSkeleton;
